import { useEffect, useRef, useCallback } from 'react';
import { useNotifications } from './useNotifications';
import { ChatMessage } from '@/types/chat.types';

interface UseMessageReadTrackingOptions {
  conversationId: string;
  messages: ChatMessage[];
  enabled?: boolean;
}

/**
 * Custom hook for tracking when messages are viewed and marking them as read
 * Uses Intersection Observer API to detect when messages enter the viewport
 */
export function useMessageReadTracking({
  conversationId,
  messages,
  enabled = true
}: UseMessageReadTrackingOptions) {
  const { markMessageAsRead, markConversationAsRead } = useNotifications();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Track which messages have been marked as read in this session
  const hasBeenMarkedAsRead = useCallback((messageId: string) => {
    return markedAsReadRef.current.has(messageId);
  }, []);

  // Mark a single message as read
  const markSingleMessageAsRead = useCallback((messageId: string) => {
    if (!enabled || hasBeenMarkedAsRead(messageId)) {
      return;
    }

    markedAsReadRef.current.add(messageId);
    markMessageAsRead(messageId);
  }, [enabled, markMessageAsRead, hasBeenMarkedAsRead]);

  // Mark entire conversation as read
  const markAllMessagesAsRead = useCallback(() => {
    if (!enabled) {
      return;
    }

    markConversationAsRead(conversationId);

    // Mark all visitor messages as read in local tracking
    messages
      .filter(msg => msg.sender_type === 'visitor' && !msg.read_by_agent_at)
      .forEach(msg => markedAsReadRef.current.add(msg.id));
  }, [enabled, conversationId, messages, markConversationAsRead]);

  // Setup intersection observer to detect when messages become visible
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Cleanup function
    const cleanup = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      // Clear all pending timeouts
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute('data-message-id');
            const senderType = entry.target.getAttribute('data-sender-type');
            const isRead = entry.target.getAttribute('data-is-read') === 'true';

            // Only track visitor messages that haven't been read
            if (messageId && senderType === 'visitor' && !isRead && !hasBeenMarkedAsRead(messageId)) {
              // Clear existing timeout for this message if any
              const existingTimeout = timeoutsRef.current.get(messageId);
              if (existingTimeout) {
                clearTimeout(existingTimeout);
              }

              // Wait 1.5 seconds before marking as read to ensure user actually saw it
              const timeout = setTimeout(() => {
                markSingleMessageAsRead(messageId);
                timeoutsRef.current.delete(messageId);
              }, 1500);

              timeoutsRef.current.set(messageId, timeout);
            }
          } else {
            // Message left viewport, cancel the pending read mark
            const messageId = entry.target.getAttribute('data-message-id');
            if (messageId) {
              const timeout = timeoutsRef.current.get(messageId);
              if (timeout) {
                clearTimeout(timeout);
                timeoutsRef.current.delete(messageId);
              }
            }
          }
        });
      },
      {
        root: null, // Use viewport as root
        rootMargin: '0px',
        threshold: 0.8 // Message must be 80% visible
      }
    );

    return cleanup;
  }, [enabled, markSingleMessageAsRead, hasBeenMarkedAsRead]);

  // Observe new message elements
  const observeMessage = useCallback((element: HTMLElement | null) => {
    if (!element || !observerRef.current || !enabled) {
      return;
    }

    observerRef.current.observe(element);
  }, [enabled]);

  // Unobserve a message element
  const unobserveMessage = useCallback((element: HTMLElement | null) => {
    if (!element || !observerRef.current) {
      return;
    }

    observerRef.current.unobserve(element);
  }, []);

  return {
    observeMessage,
    unobserveMessage,
    markAllMessagesAsRead,
    hasBeenMarkedAsRead
  };
}
